import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../../components/layout/MainLayout';
import api from '../../../utils/api';

const EmailCampaignHistory = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    try {
      const res = await api('/email-automation/campaigns');
      if (!res.ok) throw new Error('Failed to fetch campaigns');
      const data = await res.json();
      setCampaigns(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const filteredCampaigns = campaigns.filter(camp => {
    const matchesSearch = camp.subject.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || camp.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <MainLayout>
      <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <div>
            <button 
              onClick={() => navigate('/automation')} 
              style={{ marginBottom: '8px', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-secondary)', padding: 0 }}
            >
              ← Back to Automation Hub
            </button>
            <h1 style={{ fontSize: '24px', fontWeight: 'bold' }}>📨 Bulk Email History</h1>
          </div>
          <button 
            onClick={() => navigate('/automation/email/send')}
            style={{ padding: '10px 20px', background: 'var(--color-brand-primary)', color: 'white', borderRadius: '8px', fontWeight: 'bold', border: 'none', cursor: 'pointer', transition: 'opacity 0.2s' }}
            onMouseOver={(e) => e.currentTarget.style.opacity = '0.9'}
            onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
          >
            + New Campaign
          </button>
        </div>

        <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', alignItems: 'center' }}>
          <input 
            type="text"
            placeholder="Search by subject..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ padding: '10px 16px', borderRadius: '8px', border: '1px solid var(--color-border-primary)', width: '300px', outline: 'none' }}
          />
          
          <div style={{ display: 'flex', gap: '8px', background: 'white', padding: '4px', borderRadius: '8px', border: '1px solid var(--color-border-primary)' }}>
            {['all', 'completed', 'failed', 'running'].map(status => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                style={{
                  padding: '6px 16px',
                  borderRadius: '6px',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: '500',
                  textTransform: 'capitalize',
                  background: statusFilter === status ? 'var(--color-background-secondary)' : 'transparent',
                  color: statusFilter === status ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
                  transition: 'all 0.2s'
                }}
              >
                {status}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--color-text-tertiary)' }}>Loading campaigns...</div>
        ) : filteredCampaigns.length === 0 ? (
          <div style={{ padding: '60px', textAlign: 'center', background: 'white', borderRadius: '12px', border: '1px dashed var(--color-border-primary)', color: 'var(--color-text-tertiary)' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📨</div>
            <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: 'var(--color-text-primary)', marginBottom: '8px' }}>No campaigns found</h3>
            <p>Start your first bulk email campaign to see it here.</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '16px' }}>
            {filteredCampaigns.map(camp => (
              <div 
                key={camp._id} 
                onClick={() => navigate(`/automation/email/history/${camp._id}`)}
                style={{ 
                  background: 'white', 
                  borderRadius: '12px', 
                  border: '1px solid var(--color-border-primary)', 
                  padding: '20px',
                  cursor: 'pointer',
                  transition: 'transform 0.1s, box-shadow 0.1s',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.05)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                    <h3 style={{ fontSize: '16px', fontWeight: 'bold', margin: 0 }}>{camp.subject}</h3>
                    <span style={{ 
                      padding: '2px 8px', 
                      borderRadius: '12px', 
                      fontSize: '11px',
                      fontWeight: '600',
                      background: camp.status === 'completed' ? '#dcfce7' : camp.status === 'failed' ? '#fee2e2' : '#f1f5f9',
                      color: camp.status === 'completed' ? '#166534' : camp.status === 'failed' ? '#991b1b' : '#475569'
                    }}>
                      {camp.status.toUpperCase()}
                    </span>
                  </div>
                  <div style={{ fontSize: '13px', color: 'var(--color-text-secondary)', display: 'flex', gap: '16px' }}>
                    <span>📅 {new Date(camp.createdAt).toLocaleString()}</span>
                    <span>👤 By {camp.createdByName}</span>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '8px' }}>
                  <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '8px 12px', textAlign: 'center', minWidth: '70px' }}>
                    <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#0f172a' }}>{camp.stats?.totalSelected || 0}</div>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>Total</div>
                  </div>
                  <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '8px 12px', textAlign: 'center', minWidth: '70px' }}>
                    <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#166534' }}>{camp.stats?.sent || 0}</div>
                    <div style={{ fontSize: '11px', color: '#166534' }}>Sent</div>
                  </div>
                  <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '8px 12px', textAlign: 'center', minWidth: '70px' }}>
                    <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#991b1b' }}>{camp.stats?.failed || 0}</div>
                    <div style={{ fontSize: '11px', color: '#991b1b' }}>Failed</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default EmailCampaignHistory;
